import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Group,
    Modal,
    NumberInput,
    Radio,
    Select,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from '@mantine/core';
import { Info, Warning } from '@phosphor-icons/react';

import { getEntityMasterFilterOptions } from '@api/entity-attributes';
import {
    createMaterial,
    DEFAULT_MATERIAL_UNIT,
    buildUnitOptions,
    type Material,
    type MaterialType,
} from '@api/materials';
import { createVendorMaterialMapping, listVendors } from '@api/vendors';
import useApp from '@providers/App/useApp';
import { readApiError } from '@utils/apiError';
import { toOptionalNumber } from '@utils/number';
import {
    COMMON_GST_RATES,
    validateGstRate,
    validateHsnCode,
} from '@utils/tax';

interface AddMaterialModalProps {
    opened: boolean;
    onClose: () => void;
    /**
     * Fired after a successful create, carrying the row the server wrote. The grid ignores
     * it and reloads; the product form and drawer use it to put the new material straight
     * onto a BOM line.
     */
    onCreated: (material: Material) => void;
    /** Opens the new material's own page; offered as a second button when set. */
    onOpen?: (materialId: string) => void;
}

const AddMaterialModal: React.FC<AddMaterialModalProps> = ({
    opened,
    onClose,
    onCreated,
    onOpen,
}) => {
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState('');
    // No default: an unattended RM would silently misclassify, which is exactly the
    // bug the WMS loader already has.
    const [materialType, setMaterialType] = useState<MaterialType | null>(null);
    const [cost, setCost] = useState<string | number>('');
    /*
     * Starts EMPTY and must be filled. Prefilled with 'pcs', the suggestion list filtered
     * itself down to the one unit matching that text, and people read the box as "pcs is
     * the only unit" - a brand that weighs its pouches never found kg. Blank used to save
     * as pcs silently, which is the same misclassification by another route, so the form
     * now refuses to save without one.
     */
    const [unit, setUnit] = useState<string>('');
    const [gstRate, setGstRate] = useState<string>('');
    const [hsnCode, setHsnCode] = useState('');
    /*
     * Who supplies it, mapped on the spot. Optional: the material is real whether or not
     * the brand has picked a supplier yet, and the mapping can be added later. The rate is
     * the mapping's, not the material's own cost above.
     */
    const [vendorId, setVendorId] = useState<string | null>(null);
    const [vendorRate, setVendorRate] = useState<string | number>('');
    // Only an admin may put a rate on the supplier mapping; the server refuses anyone else's.
    const { currentUser } = useApp();
    const isAdmin = currentUser?.role === 'ADMIN';
    /** Set once the material itself is saved, so a retry only repeats the mapping. */
    const [savedMaterialId, setSavedMaterialId] = useState<string | null>(null);

    const [categories, setCategories] = useState<string[]>([]);
    const [unitOptions, setUnitOptions] = useState<string[]>(
        buildUnitOptions([]),
    );
    const [vendorOptions, setVendorOptions] = useState<{ value: string; label: string }[]>([]);

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const reset = () => {
        setCode('');
        setName('');
        setCategory('');
        setMaterialType(null);
        setCost('');
        setUnit('');
        setGstRate('');
        setHsnCode('');
        setVendorId(null);
        setVendorRate('');
        setSavedMaterialId(null);
        setFieldErrors({});
        setSaveError(null);
        setSaving(false);
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    // Category and unit options come from the entity-master filter options, which
    // already returns merchant-scoped DISTINCT values for every filterable column —
    // so this needs no bespoke picker endpoint.
    useEffect(() => {
        if (!opened) return;

        void getEntityMasterFilterOptions('MATERIAL')
            .then((options) => {
                setCategories(options.category ?? []);
                setUnitOptions(buildUnitOptions(options.unit ?? []));
            })
            .catch(() => {
                setCategories([]);
                setUnitOptions(buildUnitOptions([]));
            });

        // Every purchase vendor, not a filtered subset: nothing on a vendor row says
        // "this one supplies material", and the person adding it knows who sent it.
        void listVendors()
            .then((vendors) =>
                setVendorOptions(
                    vendors.map((v) => ({
                        value: v.vendorId,
                        label: v.name && v.name !== v.vendorId ? `${v.name} — ${v.vendorId}` : v.vendorId,
                    })),
                ),
            )
            .catch(() => setVendorOptions([]));
    }, [opened]);

    const validate = (): boolean => {
        const errors: Record<string, string> = {};
        if (!code.trim()) errors.code = 'Material code is required';
        if (!name.trim()) errors.name = 'Name is required';
        if (!category.trim()) errors.category = 'Category is required';
        if (!materialType) errors.materialType = 'Choose RM, PM or STAGE';
        if (!unit.trim()) errors.unit = 'Unit is required';

        const gstError = validateGstRate(gstRate);
        if (gstError) errors.gstRate = gstError;

        const hsnError = validateHsnCode(hsnCode);
        if (hsnError) errors.hsnCode = hsnError;

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (openAfter = false) => {
        if (!validate() || !materialType) return;

        setSaving(true);
        setSaveError(null);

        // Two writes, two owners: the material in its catalogue, then the mapping in the
        // vendor's. The first is skipped on a retry after the second failed, or the retry
        // would 409 on the code it just created.
        let materialId = savedMaterialId;
        if (!materialId) {
            try {
                const created = await createMaterial({
                    code: code.trim(),
                    name: name.trim(),
                    category: category.trim(),
                    materialType,
                    cost: toOptionalNumber(cost),
                    unit: unit.trim(),
                    gstRate: toOptionalNumber(gstRate),
                    hsnCode: hsnCode.trim() || undefined,
                });
                materialId = created.materialId;
                setSavedMaterialId(materialId);
                onCreated(created);
            } catch (err) {
                setSaveError(
                    readApiError(err, 'Could not add the material. Please try again.'),
                );
                setSaving(false);
                return;
            }
        }

        if (vendorId) {
            try {
                await createVendorMaterialMapping({
                    materialId,
                    vendorId,
                    cost: toOptionalNumber(vendorRate),
                });
            } catch (err) {
                setSaveError(
                    `The material was added, but mapping it to the supplier failed: ${readApiError(
                        err,
                        'please try again',
                    )}`,
                );
                setSaving(false);
                return;
            }
        }

        handleClose();
        if (openAfter) onOpen?.(materialId);
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Add material"
            size="md"
            centered
        >
            <Stack gap="md">
                <TextInput
                    label="Material code"
                    description="Also used as the material ID. Must be unique."
                    placeholder="e.g. RM-Green_Cotton_Fabric"
                    value={code}
                    onChange={(e) => setCode(e.currentTarget.value)}
                    error={fieldErrors.code}
                    withAsterisk
                    data-autofocus
                />

                <TextInput
                    label="Name"
                    placeholder="e.g. Green Cotton Fabric"
                    value={name}
                    onChange={(e) => setName(e.currentTarget.value)}
                    error={fieldErrors.name}
                    withAsterisk
                />

                <Radio.Group
                    label="Type"
                    value={materialType ?? ''}
                    onChange={(v) => setMaterialType(v as MaterialType)}
                    error={fieldErrors.materialType}
                    withAsterisk
                >
                    <Group mt="xs" gap="lg">
                        <Radio value="RM" label="Raw material (RM)" />
                        <Radio value="PM" label="Packaging material (PM)" />
                        <Radio value="STAGE" label="Stage / intermediate (STAGE)" />
                    </Group>
                </Radio.Group>

                <Autocomplete
                    label="Category"
                    placeholder="Pick one or type a new category"
                    data={categories}
                    value={category}
                    onChange={setCategory}
                    error={fieldErrors.category}
                    withAsterisk
                />

                <Group grow align="flex-start">
                    <Autocomplete
                        label="Unit"
                        placeholder={`e.g. ${DEFAULT_MATERIAL_UNIT}, kg, m`}
                        data={unitOptions}
                        value={unit}
                        onChange={setUnit}
                        error={fieldErrors.unit}
                        withAsterisk
                    />
                    <NumberInput
                        label="Cost"
                        placeholder="Optional"
                        value={cost}
                        onChange={setCost}
                        min={0}
                        decimalScale={2}
                    />
                </Group>

                <Group grow align="flex-start">
                    <Autocomplete
                        label="GST %"
                        placeholder="e.g. 18"
                        data={COMMON_GST_RATES}
                        value={gstRate}
                        onChange={setGstRate}
                        error={fieldErrors.gstRate}
                    />
                    <TextInput
                        label="HSN/SAC code"
                        placeholder="Optional"
                        value={hsnCode}
                        onChange={(e) => setHsnCode(e.currentTarget.value)}
                        error={fieldErrors.hsnCode}
                    />
                </Group>

                <Group grow align="flex-start">
                    <Select
                        label="Supplier"
                        description="Optional — maps this vendor to the material"
                        placeholder="Choose a vendor"
                        data={vendorOptions}
                        value={vendorId}
                        onChange={setVendorId}
                        searchable
                        clearable
                    />
                    <Tooltip label="Admins only" disabled={isAdmin} withArrow>
                        <div>
                            <NumberInput
                                label="Agreed rate"
                                description="What this supplier charges. Optional."
                                placeholder="Optional"
                                value={vendorRate}
                                onChange={setVendorRate}
                                min={0}
                                decimalScale={2}
                                disabled={!vendorId || !isAdmin}
                            />
                        </div>
                    </Tooltip>
                </Group>

                <Alert color="blue" icon={<Info size={16} />}>
                    <Text size="xs">
                        This adds the material to your catalog. It won&apos;t show stock
                        until a feed reports it, and won&apos;t appear in planning until
                        it&apos;s mapped into a bill of materials.
                    </Text>
                </Alert>

                {saveError && (
                    <Alert color="red" icon={<Warning size={16} />}>
                        {saveError}
                    </Alert>
                )}

                <Group justify="flex-end" mt="xs">
                    <Button variant="subtle" color="gray" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        variant={onOpen ? 'default' : 'filled'}
                        onClick={() => void handleSubmit(false)}
                        loading={saving}
                    >
                        {savedMaterialId ? 'Retry mapping' : 'Add material'}
                    </Button>
                    {onOpen && !savedMaterialId && (
                        <Button onClick={() => void handleSubmit(true)} loading={saving}>
                            Add and open
                        </Button>
                    )}
                </Group>
            </Stack>
        </Modal>
    );
};

export default AddMaterialModal;
